import { htmlToText } from '@helpers/utils';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import FormField from '@components/Common/FormField';
import { Box, Button, Chip, Divider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';
import './NotesSection.scss';
import { formatDate } from '@helpers/date';

export type NoteVisibility = 'INTERNAL' | 'SHARED';

export interface NoteItem {
  id?: string;
  note: string;
  changedBy: string;
  date: string;
  /** INTERNAL (HR/Super Admin only) or SHARED (also visible to the premium employer). */
  visibility?: string;
}

interface NotesSectionProps {
  notes: NoteItem[];
  isEditMode?: boolean;
  onAdd: (noteHtml: string, visibility?: NoteVisibility) => void;
  onDelete?: (noteId: string) => void;
  /** When true, the composer shows an Internal/Shared toggle (Premium Managed Hiring, C6). */
  showVisibilityToggle?: boolean;
}

const NotesSection: React.FC<NotesSectionProps> = ({
  notes,
  isEditMode = false,
  onAdd,
  onDelete,
  showVisibilityToggle = false,
}) => {
  const [newNote, setNewNote] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('INTERNAL');

  const handleAdd = () => {
    if (!newNote.trim()) return;
    onAdd(newNote, showVisibilityToggle ? visibility : undefined);
    setNewNote('');
  };

  return (
    <Box className="notes-section">
      {/* Add note form — visible in edit mode */}
      {isEditMode && (
        <Box className="notes-section__add-form">
          {/* Was a rich-text editor. A note is a line of prose, not a document, and the HTML it
              emitted leaked into the activity timeline as literal <div><br></div>. TextEditor is
              untouched and still used for company About and job descriptions, where markup is
              wanted. */}
          <FormField
            required={false}
            multiline
            name="new-note"
            label=""
            placeholder="Add a note..."
            value={newNote}
            textFieldProps={{ rows: undefined, minRows: 3 }}
            onChange={(_n, v) => setNewNote(String(v))}
          />
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            {showVisibilityToggle ? (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={visibility}
                onChange={(_, v) => v && setVisibility(v as NoteVisibility)}
              >
                <ToggleButton value="INTERNAL">
                  <Tooltip title="Visible only to HR & Super Admins">
                    <span>Internal</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="SHARED">
                  <Tooltip title="Also visible to the Premium Hiring client">
                    <span>Shared</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            ) : (
              <span />
            )}
            <Button
              disabled={!newNote.trim()}
              endIcon={<Add />}
              size="small"
              variant="outlined"
              onClick={handleAdd}
            >
              Add Note
            </Button>
          </Box>
          <Divider sx={{ mt: 2, mb: 2 }} />
        </Box>
      )}

      {/* Notes list */}
      {Array.isArray(notes) && notes.length > 0 ? (
        <Box className="notes-section__list">
          {notes.map((note, index) => (
            <Box
              key={note.id || index}
              className={`notes-section__item ${index % 2 === 0 ? 'even' : 'odd'}`}
            >
              <Box sx={{ flex: 1 }}>
                {/* Existing notes are stored HTML; htmlToText spends the block tags as newlines
                    rather than dropping them, so an old multi-line note stays multi-line. */}
                <Box className="note-text">{htmlToText(note.note || '')}</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <Chip
                    label={note.changedBy}
                    size="small"
                    // --acc, not --color-primary: the brand blue is a fill,
                    // and as text on its own tint over graphite it is 2.85:1.
                    sx={{ bgcolor: 'var(--acc-tint)', color: 'var(--acc)' }}
                  />
                  {note.visibility && (
                    <Chip
                      label={note.visibility === 'SHARED' ? 'Shared' : 'Internal'}
                      size="small"
                      sx={{
                        bgcolor:
                          note.visibility === 'SHARED'
                            ? 'var(--color-stage-won-bg)'
                            : 'var(--color-background-muted)',
                        color:
                          note.visibility === 'SHARED'
                            ? 'var(--color-stage-won-text)'
                            : 'var(--color-text-muted)',
                      }}
                    />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(note.date)}
                  </Typography>
                </Box>
              </Box>

              {isEditMode && note.id && onDelete && (
                <Button
                  className="delete-button"
                  color="error"
                  endIcon={<Delete />}
                  size="small"
                  variant="outlined"
                  onClick={() => onDelete(note.id as string)}
                >
                  Delete
                </Button>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography color="textSecondary" variant="body2">
            No notes yet.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NotesSection;
